import { useState } from 'react';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { AxiosResponse } from 'axios';
import { StyledButton } from 'components/Buttons';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { RatesTab, reactPaths } from 'root/pathnames';
import { appToast } from 'utils/toast.utils';
import { isSuccessful } from '../../RatesForm/ratesForm.utils';
import * as S from '../RatesTable.css';
import { useDeleteStormRate } from 'api/rates';

interface ActionButtonsProps {
  selectedRows: Set<number>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
  refreshTable: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  selectedRows,
  setSelectedRows,
  refreshTable,
}) => {
  const { t } = useTranslation(['misc', 'rates']);
  const navigate = useNavigate();
  const { tab: ratesTab } = useParams<{ tab: RatesTab }>();
  const { deleteStormRate } = useDeleteStormRate();

  const ADD_RATE_BUTTON = t('addRate', { ns: 'rates' });
  const DELETE_BUTTON = t('button.delete', { ns: 'misc' });
  const DELETE_RATE_BODY = t('modal.deleteRateBody', { ns: 'misc' });
  const DELETE_FAILURE = t('message.deleteRateFailure', { ns: 'misc' });
  const DELETE_SUCCESS = t('message.deleteRateSuccess', { ns: 'misc' });
  const DELETE_HEADER = t('modal.deleteEntityHeading', { ns: 'misc' });
  const CANCEL_BUTTON = t('button.cancel', { ns: 'misc' });

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const createRateHandler = () => {
    navigate(generatePath(reactPaths.ratesForm, { tab: ratesTab }));
  };

  const handleModalClose = () => {
    setOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      const responses: AxiosResponse[] = await Promise.all(
        Array.from(selectedRows).map((id) => deleteStormRate(String(id))),
      );
      const allSuccessful = responses.every((response) =>
        isSuccessful(response),
      );
      if (allSuccessful) {
        setSelectedRows(new Set());
        appToast.success(DELETE_SUCCESS);
        refreshTable();
      } else {
        appToast.error(DELETE_FAILURE);
      }
    } catch (error) {
      console.error('Error deleting rates:', error);
      appToast.error(DELETE_FAILURE);
    } finally {
      setLoading(false);
      handleModalClose();
    }
  };

  const deleteRateHandler = () => {
    setOpen(true);
  };

  return (
    <>
      <S.ActionsContainer>
        <StyledButton $primary onClick={createRateHandler}>
          {ADD_RATE_BUTTON}
        </StyledButton>
        {/* <StyledButton
          $primary
          onClick={deleteRateHandler}
          disabled={selectedRows.size === 0}
        >
          {DELETE_BUTTON}
        </StyledButton> */}
      </S.ActionsContainer>
      <S.StyledDialog open={open} onClose={handleModalClose}>
        <LoaderFullScreen loading={loading} />
        <DialogTitle>{DELETE_HEADER}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>{DELETE_RATE_BODY}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton $primary onClick={handleDeleteConfirm}>
            {DELETE_BUTTON}
          </StyledButton>
          <StyledButton onClick={handleModalClose}>
            {CANCEL_BUTTON}
          </StyledButton>
        </DialogActions>
      </S.StyledDialog>
    </>
  );
};

export default ActionButtons;
